import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Input, Textarea, toast } from "@sagot/ui";
import { blankDraft, type QaDraft } from "./qa-model";

const K = "onboarding.knowledge";

interface QaTabProps {
  drafts: QaDraft[];
  example: string | undefined;
  /** Rows the last save attempt rejected, by draft id. */
  problems: ReadonlySet<string>;
  saving: boolean;
  onChange: (drafts: QaDraft[]) => void;
  onSave: () => void;
}

function SavedRow({ draft }: { draft: QaDraft }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <Icon name="check-circle" className="mt-0.5 size-4 shrink-0 text-success" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">{draft.question}</p>
        <p className="line-clamp-2 text-xs text-fg-muted">{draft.answer}</p>
      </div>
      <Badge tone="success">{t(`${K}.qaSavedBadge`)}</Badge>
    </li>
  );
}

/**
 * Write Q&A: every complete pair becomes its own Q&A source in the Knowledge Base on Save / Next
 * (the same shape as the Knowledge Base page). Saved pairs stay listed, read-only.
 */
export function QaTab({ drafts, example, problems, saving, onChange, onSave }: QaTabProps) {
  const { t } = useTranslation();
  const update = (id: string, patch: Partial<QaDraft>) => { onChange(drafts.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft))); };
  const open = drafts.filter((draft) => draft.sourceId === undefined);
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">{t(`${K}.qaNote`)}</p>
      <ul className="space-y-3" aria-label={t(`${K}.qaListLabel`)}>
        {drafts.map((draft, index) =>
          draft.sourceId !== undefined ? (
            <SavedRow key={draft.id} draft={draft} />
          ) : (
            <li key={draft.id} className="rounded-lg border border-line bg-surface-2 p-4">
              <div className="flex items-start gap-3">
                <span className="mt-2 text-xs font-bold text-primary-ink" aria-hidden="true">Q</span>
                <Input
                  aria-label={`${t(`${K}.question`)} ${String(index + 1)}`}
                  aria-invalid={problems.has(draft.id) || undefined}
                  placeholder={t(`${K}.questionPlaceholder`, { example: example ?? t(`${K}.questionExample`) })}
                  value={draft.question}
                  onChange={(event) => { update(draft.id, { question: event.target.value }); }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  className="mt-1"
                  aria-label={`${t(`${K}.removePair`)} ${String(index + 1)}`}
                  onClick={() => {
                    const rest = drafts.filter((candidate) => candidate.id !== draft.id);
                    onChange(rest.some((candidate) => candidate.sourceId === undefined) ? rest : [...rest, blankDraft()]);
                    toast.info(t(`${K}.pairRemoved`));
                  }}
                >
                  <Icon name="trash" />
                </Button>
              </div>
              <div className="mt-2 flex items-start gap-3">
                <span className="mt-2 text-xs font-bold text-fg-subtle" aria-hidden="true">A</span>
                <Textarea
                  className="mr-11"
                  rows={2}
                  aria-label={`${t(`${K}.answer`)} ${String(index + 1)}`}
                  aria-invalid={problems.has(draft.id) || undefined}
                  placeholder={t(`${K}.answerPlaceholder`)}
                  value={draft.answer}
                  onChange={(event) => { update(draft.id, { answer: event.target.value }); }}
                />
              </div>
            </li>
          ),
        )}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => { onChange([...drafts, blankDraft()]); }}>
          <Icon name="plus" />
          {t(`${K}.addPair`)}
        </Button>
        <Button size="sm" loading={saving} disabled={open.every((draft) => !draft.question.trim() && !draft.answer.trim())} onClick={onSave}>
          <Icon name="check" />
          {t(`${K}.qaSave`)}
        </Button>
      </div>
    </div>
  );
}
